import { Injectable } from '@angular/core';
import {
  CartItemPayload,
  CartItemResponse,
  ComplexWallItem,
  WallItem,
} from '../common/types/wall-item';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import detectEthereumProvider from '@metamask/detect-provider';

@Injectable({
  providedIn: 'root',
})
export class TilesService {
  private cartItems: ComplexWallItem[] = [];
  private cartItemsSub: Subject<ComplexWallItem[]> = new BehaviorSubject<
    ComplexWallItem[]
  >(this.cartItems);
  public cartItems$ = this.cartItemsSub.asObservable();

  private wallItems: ComplexWallItem[] = [];
  private wallItemsSub: Subject<ComplexWallItem[]> = new BehaviorSubject<
    ComplexWallItem[]
  >(this.wallItems);
  public wallItems$ = this.wallItemsSub.asObservable();

  constructor(private http: HttpClient) {}

  private static getComplexWallItem(
    items: Record<string, WallItem[]>
  ): ComplexWallItem[] {
    return Object.entries(items).map((wallItem) => ({
      mainWallItemId: wallItem[0],
      tiles: [...wallItem[1]],
    }));
  }

  public fetchWallTiles(): Observable<ComplexWallItem[]> {
    return this.http
      .get<Record<string, WallItem[]>>(`${environment.baseApi}/client/tiles`)
      .pipe(
        map((wallItems) => TilesService.getComplexWallItem(wallItems)),
        tap((wallItems: ComplexWallItem[]) => {
          if (wallItems) {
            this.wallItems = wallItems;
            this.wallItemsSub.next(this.wallItems);
          }
        })
      );
  }

  public emptyCart(): void {
    this.cartItems = [];
    this.cartItemsSub.next(this.cartItems);
  }

  public preCheckout(): Observable<void> {
    return this.http.post<void>(
      `${environment.baseApi}/client/my-cart-checkout`,
      {}
    );
  }

  public addToCart(item: CartItemPayload): Observable<CartItemResponse> {
    return this.http
      .post<CartItemResponse>(`${environment.baseApi}/client/my-cart`, item)
      .pipe(
        tap((response: CartItemResponse) => {
          const mappedCartItems: ComplexWallItem = {
            mainWallItemId: response.mainWallItemId,
            tiles: response.tiles.map((tile) => ({
              id: tile,
              image: response.image,
              userId: response.userId,
              price: response.price / response.tiles.length,
            })),
          };
          this.addItemToCart(mappedCartItems);
        })
      );
  }

  public fetchCartItems(
    fromCache?: boolean
  ): Observable<ComplexWallItem[] | null> {
    return this.cartItems?.length > 0 && fromCache
      ? of(this.cartItems)
      : this.http
          .get<Record<string, WallItem[]>>(
            `${environment.baseApi}/client/my-cart`
          )
          .pipe(
            map((wallItems) => TilesService.getComplexWallItem(wallItems)),
            tap((cartItems: ComplexWallItem[]) => {
              this.cartItems = cartItems;
              this.cartItemsSub.next(this.cartItems);
            }),
            catchError(() => of(null))
          );
  }

  public removeCartItem(itemId: string): Observable<boolean> {
    return this.http
      .delete<boolean>(`${environment.baseApi}/client/my-cart/${itemId}`)
      .pipe(
        tap(() => {
          this.removeItemFromCart(itemId);
        })
      );
  }

  public sendTransaction(transactionId: string): Observable<boolean> {
    return this.http.post<boolean>(
      `${environment.baseApi}/client/me/transactions`,
      {
        transactionId,
      }
    );
  }

  public editCartItem(
    cartItemId: string,
    item: CartItemPayload
  ): Observable<WallItem> {
    const isCartItem = this.cartItems.some(
      (ci) => ci.mainWallItemId === cartItemId
    );
    return this.http
      .put<WallItem>(
        `${environment.baseApi}/client/my-cart/${cartItemId}`,
        item
      )
      .pipe(
        tap((res: WallItem) => {
          if (res) {
            isCartItem
              ? this.updateCartItemArray(cartItemId, res)
              : this.updateWallItemArray(cartItemId, res);
          }
        })
      );
  }

  public mint(tiles: number[]): Observable<any> {
    return this.http.get('/assets/matic/TESTLNDVWOS.json').pipe(
      switchMap(async (contractJSON: any) => {
        const p = await detectEthereumProvider();
        const provider = new (window as any).Web3(p);
        const gasPrice = await provider.eth.getGasPrice();
        const contract = new provider.eth.Contract(
          contractJSON.abi,
          environment.contractAddress,
          {
            from: provider.currentProvider.selectedAddress,
          }
        );
        return contract.methods.mint(tiles).send({
          value: 10000000000000000 * tiles.length,
          gasPrice,
        });
      }),
      switchMap(({ transactionHash }) => this.sendTransaction(transactionHash))
    );
  }

  private updateCartItemArray(itemId: string, data: any): void {
    const editedItemIndex = this.cartItems.findIndex(
      (item) => item.mainWallItemId === itemId
    );
    const updatedTiles = {
      ...this.cartItems[editedItemIndex],
      tiles: [
        ...this.cartItems[editedItemIndex].tiles.map((ci) => ({
          ...ci,
          image: data.image,
        })),
      ],
    };
    this.cartItems[editedItemIndex] = {
      ...updatedTiles,
      ...data,
    };
    this.cartItemsSub.next(this.cartItems);
  }

  private updateWallItemArray(itemId: string, data: any): void {
    const editedItemIndex = this.wallItems.findIndex(
      (item) => item.mainWallItemId === itemId
    );
    const updatedTiles = {
      ...this.wallItems[editedItemIndex],
      tiles: [
        ...this.wallItems[editedItemIndex].tiles.map((wi) => ({
          ...wi,
          image: data.image,
        })),
      ],
    };
    this.wallItems[editedItemIndex] = {
      ...updatedTiles,
      ...data,
    };
    this.wallItemsSub.next(this.wallItems);
  }

  private addItemToCart(item: ComplexWallItem): void {
    this.cartItems.push(item);

    this.cartItemsSub.next(this.cartItems);
  }

  private removeItemFromCart(itemId: string): void {
    this.cartItems = this.cartItems.filter(
      (c: ComplexWallItem) => c.mainWallItemId !== itemId
    );
    this.cartItemsSub.next(this.cartItems);
  }
}
